import React from "react";

import Booking from "../Components/CustomerStepper/Steps/Booking";
import Manual from "../Components/CustomerStepper/Steps/Manual";
import Qualifying from "../Components/CustomerStepper/Steps/Qualifying";
import Search from "../Components/CustomerStepper/Steps/Search/Search";
import UserSessionContextDispositionStep from "../V2/Playbook/Disposition/Compositions/SessionContextDispositionStep";
import ComposedInterestsStep from "../V2/Playbook/Interests/Compositions/ComposedAreasOfInterestStep";
import ComposedIntroStep from "../V2/Playbook/Intro/Compositions/ComposedIntroStep";
import ComposedMedicalStep from "../V2/Playbook/Medical/Compositions/ComposedMedicalStep";

export enum StepId {
  Search = 0,
  Manual = 1,
  Intro = 2,
  Interests = 3,
  Qualifying = 4,
  Medical = 5,
  Booking = 6,
  Disposition = 7
}

/** A 'step' within the stepper component. */
export interface Step {
  /** The enum step value associated with this object. */
  id: StepId;
  /** The plain text name of this step. */
  name: string;
  /** Flag indicating if the note edit field is hidden on this step. */
  isNoteFieldHidden: boolean;
  /** Flag indicating if the preview customer should be shown in the ribbon on this step. */
  isPreviewCustomerShown: boolean;
  /** Flag indicating if the application will save context properties to the server on this step. */
  isSaveStep: boolean;
  /** A flag indicating if the application does not consider this step to count as 'engaging' with the session.
   * @remarks This flag overrides the `isSaveStep` flag, until a step is reached where `isShortCircuit` is false.
   */
  isShortCircuit: boolean;
  /** Returns a flag indicating if the step should be included in the stepper for this lead. */
  getShowStep?: (isLeadManual: boolean, isLeadOutbound: boolean) => boolean;
  /** Returns a list of messages to be displayed regarding why the step is disabled. */
  getDisabledReasons: (isSearchComplete: boolean, isLeadOutbound: boolean, bookingDisquals: string[]) => string[];
  /** Returns a flag indicating if the step should be marked as completed. */
  getCompleted?: (isSearchComplete: boolean) => boolean;
  /** Returns a flag indicating if the step should be shown with error styling. */
  getError?: (isSearchComplete: boolean, isLeadOutbound: boolean, bookingDisquals: string[]) => boolean;
  /** The function component which should be rendered when this step is active. */
  Component: React.FC;
}

/** A dictionary mapping of the stepper values to its properties. */
export const Steps: Record<StepId, Step> = {
  [StepId.Search]: {
    id: StepId.Search,
    name: "Search",
    isNoteFieldHidden: true,
    isPreviewCustomerShown: true,
    isShortCircuit: true,
    isSaveStep: false,
    Component: Search,
    getShowStep: (isLeadManual, isLeadOutbound) => !isLeadManual && !isLeadOutbound,
    getDisabledReasons: () => []
  },
  [StepId.Manual]: {
    id: StepId.Manual,
    name: "Manual",
    isNoteFieldHidden: true,
    isPreviewCustomerShown: true,
    isShortCircuit: true,
    isSaveStep: false,
    Component: Manual,
    getDisabledReasons: isSearchComplete =>
      isSearchComplete ? ["You cannot change the guest after the call has begun."] : [],
    getCompleted: isSearchCompleted => isSearchCompleted,
    getShowStep: isLeadManual => isLeadManual
  },
  [StepId.Intro]: {
    id: StepId.Intro,
    name: "Intro",
    isNoteFieldHidden: false,
    isPreviewCustomerShown: false,
    isShortCircuit: true,
    isSaveStep: true,
    Component: ComposedIntroStep,
    getDisabledReasons: (isSearchComplete, isLeadOutbound) =>
      !isSearchComplete && !isLeadOutbound ? ["You must select a guest to start the session."] : []
  },
  [StepId.Interests]: {
    id: StepId.Interests,
    name: "Interests",
    isNoteFieldHidden: false,
    isPreviewCustomerShown: false,
    isShortCircuit: false,
    isSaveStep: true,
    Component: ComposedInterestsStep,
    getDisabledReasons: (isSearchComplete, isLeadOutbound) =>
      !isSearchComplete && !isLeadOutbound ? ["You must select a guest to start the session."] : []
  },
  [StepId.Qualifying]: {
    id: StepId.Qualifying,
    name: "Qualifying",
    isNoteFieldHidden: false,
    isPreviewCustomerShown: false,
    isShortCircuit: false,
    isSaveStep: true,
    Component: Qualifying,
    getDisabledReasons: (isSearchComplete, isLeadOutbound) =>
      !isSearchComplete && !isLeadOutbound ? ["You must select a guest to start the session."] : []
  },
  [StepId.Medical]: {
    id: StepId.Medical,
    name: "Medical",
    isNoteFieldHidden: false,
    isPreviewCustomerShown: false,
    isShortCircuit: false,
    isSaveStep: true,
    Component: ComposedMedicalStep,
    getDisabledReasons: (isSearchComplete, isLeadOutbound) =>
      !isSearchComplete && !isLeadOutbound ? ["You must select a guest to start the session."] : []
  },
  [StepId.Booking]: {
    id: StepId.Booking,
    name: "Booking",
    isNoteFieldHidden: false,
    isPreviewCustomerShown: false,
    isShortCircuit: false,
    isSaveStep: true,
    Component: Booking,
    getDisabledReasons: (isSearchComplete, isLeadOutbound, bookingDisquals) =>
      !isSearchComplete && !isLeadOutbound ? ["You must select a guest to start the session."] : bookingDisquals,
    getError: (isSearchComplete, isLeadOutbound, bookingDisquals) =>
      (isSearchComplete || isLeadOutbound) && Boolean(bookingDisquals.length)
  },
  [StepId.Disposition]: {
    id: StepId.Disposition,
    name: "Disposition",
    isNoteFieldHidden: false,
    isPreviewCustomerShown: false,
    isShortCircuit: true,
    isSaveStep: true,
    Component: UserSessionContextDispositionStep as React.FC,
    getDisabledReasons: (isSearchComplete, isLeadOutbound) =>
      !isSearchComplete && !isLeadOutbound ? ["You must select a guest to start the session."] : []
  }
};
